import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Translate } from 'react-localize-redux';
import posed, { PoseGroup } from 'react-pose';
import { useSelector } from 'react-redux';
import { AutoComplete, Button } from '../../components/widgets';
import DownloadIcon from '../../resources/icons/download.svg';
import { getFeaturePermissions } from '../../selectors/permissions';
import { isUserAdNetwork } from '../../selectors/user';
import WarningMessage, { MessageTypes } from '../../teamManagement/components/WizardWarningMessage';
import { useAsync, useOutsideEventHandler } from '../../utils/customHooks';
import { download } from '../../utils/export';
import PartnerConfigService from '../service';
import Filters, { FilterOptions } from './Filters';
import { trackPartnerConfigMixpanelEvent } from './shelf/utils';
import css from './TopArea.css';

const api = new PartnerConfigService();

const PartnerSelectionAnimationItem = posed.div({
    preEnterPose: {
        x: -20,
        opacity: 0,
        transition: { duration: 150 },
    },
    enter: {
        x: 0,
        opacity: 1,
        transition: {
            x: { type: 'tween', duration: 300, ease: 'anticipate' },
        },
    },
    exit: {
        x: 20,
        opacity: 0,
        transition: { duration: 150 },
    },
});

export const PARTNER_SELECTION_DATA_TEST_ID = 'partner-selection';

// we need a variable since the linter changes setFocus={true} => setFocus
const FOCUS_ON_AUTOCOMPLETE = true;

const getExportAllFileName = () => `singular_postbacks_${new Date().toISOString()}.csv`;

export default function TopArea({
    partnersOptions = [],
    filter = null,
    filterLoading = false,
    selectedPartner = null,
    onAddSiteForPartner,
    onFilterChanged,
}) {
    const { permission, typesMap, isSingleAccessLevel } = useSelector(getFeaturePermissions);
    const readOnly = permission === typesMap.read;
    const partnerSelectionRef = useRef(null);
    const [exportAllResponse, run] = useAsync();
    const [partnerSelectionOpen, setPartnerSelectionOpen] = useState(false);
    const isPartner = useSelector(state => isUserAdNetwork(state));
    const exportAllStartTime = useRef(null);
    const exportedPartner = useRef(null);

    useOutsideEventHandler(partnerSelectionRef, () => setPartnerSelectionOpen(false), [{ name: 'mousedown' }]);

    const { data: exportAllData, isLoading: isLoadingExportAll, error: exportAllError } = exportAllResponse;

    useEffect(() => {
        if (isLoadingExportAll) {
            exportAllStartTime.current = performance.now();
        }

        if (exportAllData) {
            const { contents, filename } = exportAllData;
            download(filename || getExportAllFileName(), contents);
            trackPartnerConfigMixpanelEvent(
                'Export Button Clicked',
                exportedPartner.current ? selectedPartner : null,
                null,
                {
                    durationInMilliseconds: performance.now() - exportAllStartTime.current,
                }
            );
        }
    }, [exportAllResponse]);

    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportMenuRef = useRef(null);

    useOutsideEventHandler(exportMenuRef, () => setShowExportMenu(false), [{ name: 'mousedown' }]);

    const onExportAllClick = () => {
        setShowExportMenu(false);
        exportedPartner.current = false;
        run(api.exportAll());
    };

    const onExportPartnerClick = () => {
        setShowExportMenu(false);
        exportedPartner.current = true;
        run(api.exportAll(selectedPartner));
    };

    const onAddPartner = ({ id, label }) => {
        const mixpanelParams = { partner: label, button: 'Add Partner' };
        trackPartnerConfigMixpanelEvent('Add Clicked', null, null, mixpanelParams);

        onAddSiteForPartner(id);
    };

    return (
        <div>
            <div className={css.topArea}>
                <Filters filter={filter} filterLoading={filterLoading} onFilterChanged={onFilterChanged} />
                <div className={css.actionsContainer} ref={partnerSelectionRef}>
                    <div className={css.exportContainer} ref={exportMenuRef}>
                        <Button
                            Icon="download"
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            disabled={!!isLoadingExportAll}
                            type="secondary"
                            level="level1"
                        >
                            <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.EXPORT" />
                        </Button>
                        {showExportMenu && (
                            <ul className={css.exportMenu}>
                                <li onClick={onExportAllClick}>
                                    <DownloadIcon className={css.exportIcon} />
                                    <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.EXPORT_ALL_CONFIGURATIONS" />
                                </li>
                                <li
                                    onClick={selectedPartner ? onExportPartnerClick : undefined}
                                    className={classNames({ [css.disabled]: !selectedPartner })}
                                >
                                    <DownloadIcon className={css.exportIcon} />
                                    <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.EXPORT_THIS_PARTNER_ONLY" />
                                </li>
                            </ul>
                        )}
                    </div>
                    {isPartner || readOnly || !isSingleAccessLevel ? null : (
                        <Button Icon="plus" onClick={() => setPartnerSelectionOpen(!partnerSelectionOpen)}>
                            <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.ADD_PARTNER" />
                        </Button>
                    )}
                    <PoseGroup animateOnMount>
                        {partnerSelectionOpen && (
                            <PartnerSelectionAnimationItem key="partnerSelection">
                                <AutoComplete
                                    searchable
                                    dataTestId={PARTNER_SELECTION_DATA_TEST_ID}
                                    setFocus={FOCUS_ON_AUTOCOMPLETE}
                                    containerClass={css.newPartnerInput}
                                    // prevent the apps table sticky header from hiding the list dropdown
                                    selectOptions={{ options: partnersOptions, usePortal: true }}
                                    onChange={onAddPartner}
                                />
                            </PartnerSelectionAnimationItem>
                        )}
                    </PoseGroup>
                </div>
            </div>
            {Boolean(exportAllError) && (
                <WarningMessage
                    type={MessageTypes.ERROR}
                    duration={1000}
                    message="STATIC.PAGES.PARTNER_CONFIGURATION.EXPORT_ALL_FAILURE_MESSAGE"
                />
            )}
        </div>
    );
}

TopArea.propTypes = {
    filter: PropTypes.shape({
        text: PropTypes.string,
        option: PropTypes.oneOf(Object.values(FilterOptions)),
    }),
    partnersOptions: PropTypes.arrayOf(PropTypes.any),
    filterLoading: PropTypes.bool,
    selectedPartner: PropTypes.number,
    onAddSiteForPartner: PropTypes.func.isRequired,
    onFilterChanged: PropTypes.func.isRequired,
};
